import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  ArrowRight, ArrowLeftRight, Landmark, Users, CalendarClock, HandCoins, BarChart3, Menu, X,
} from "lucide-react";
import Logo, { LogoMark } from "@/components/Logo";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: EASE, delay }}>
      {children}
    </motion.span>
  </span>
);

const Fade = ({ children, delay = 0, className = "", y = 24 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

/* ---------- Static mock dashboard used in hero & product shot ---------- */
const MOCK = [
  { m: 0, inc: 92000, exp: 61000 },
  { m: 1, inc: 74000, exp: 58000 },
  { m: 2, inc: 163000, exp: 69500 },
  { m: 3, inc: 86000, exp: 64000 },
  { m: 4, inc: 170750, exp: 72500 },
  { m: 5, inc: 97500, exp: 66800 },
];
const fmtL = (n) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(n);

function MockChart({ h = 120 }) {
  const w = 520, max = 200000;
  const pts = (key) =>
    MOCK.map((d, i) => `${(i / (MOCK.length - 1)) * w},${h - (d[key] / max) * h}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none" style={{ height: h }}>
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1="0" x2={w} y1={h * g} y2={h * g} stroke="#0F172A" strokeOpacity="0.06" strokeDasharray="3 4" />
      ))}
      <polygon points={`0,${h} ${pts("inc")} ${w},${h}`} fill="#0055FF" fillOpacity="0.08" />
      <polyline points={pts("inc")} fill="none" stroke="#0055FF" strokeWidth="2" />
      <polyline points={pts("exp")} fill="none" stroke="#DC2626" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7" />
      {MOCK.map((d, i) => (
        <circle key={i} cx={(i / (MOCK.length - 1)) * w} cy={h - (d.inc / max) * h} r="2.5" fill="#0055FF" />
      ))}
    </svg>
  );
}

function MockDashboard({ full = false }) {
  const { t } = useI18n();
  const kpis = [
    { label: t.hero.mockBalance, v: "₺704.250", cls: "text-slate-900" },
    { label: t.hero.mockIncome, v: "₺97.500", cls: "text-positive" },
    { label: t.hero.mockExpense, v: "₺66.800", cls: "text-negative" },
    { label: t.hero.mockNet, v: "+₺30.700", cls: "text-positive" },
  ];
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_80px_-24px_rgba(15,23,42,0.25)]">
      <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
        <span className="ml-3 hidden rounded-md bg-slate-50 px-2.5 py-1 font-mono text-[10px] text-slate-400 sm:block">
          app.irafinans.com
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-medium text-emerald-600">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> {t.hero.live}
        </span>
      </div>
      <div className="flex">
        {full && (
          <div className="hidden w-44 shrink-0 flex-col gap-1 border-r border-slate-100 p-4 md:flex">
            <div className="mb-3"><LogoMark className="h-7 w-7" /></div>
            {[t.app.dashboard, t.app.transactions, t.app.banks, t.app.cari, t.app.payments, t.app.receivables, t.app.reports].map((x, i) => (
              <div key={x} className={`rounded-md px-3 py-2 text-[11px] font-medium ${i === 0 ? "bg-slate-900 text-white" : "text-slate-500"}`}>{x}</div>
            ))}
          </div>
        )}
        <div className="min-w-0 flex-1 p-5">
          <div className="flex items-baseline justify-between">
            <p className="font-display text-sm font-semibold text-slate-900">{t.hero.mockTitle}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">İRA FINANS</p>
          </div>
          <div className={`mt-4 grid gap-3 ${full ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
            {(full ? kpis : kpis.slice(1)).map((k) => (
              <div key={k.label} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                <p className="truncate text-[10px] uppercase tracking-wider text-slate-400">{k.label}</p>
                <p className={`tnum mt-1 font-display text-sm font-semibold sm:text-base ${k.cls}`}>{k.v}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-slate-100 p-3">
            <MockChart h={full ? 150 : 110} />
            <div className="mt-2 flex justify-between font-mono text-[9px] text-slate-400">
              {MOCK.map((d) => <span key={d.m}>{t.hero.months[d.m]}</span>)}
            </div>
          </div>
          {full && (
            <div className="mt-3 space-y-1.5">
              {[["Mega İnşaat — Proje Faturası", "Tahsilat", "+₺148.000"], ["Personel Maaşları", "Maaş", "−₺96.000"], ["Demir Lojistik — Taşıma", "Hizmet Geliri", "+₺62.500"]].map((r) => (
                <div key={r[0]} className="flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                  <span className="truncate text-[11px] text-slate-600">{r[0]}</span>
                  <span className="ml-auto hidden font-mono text-[10px] text-slate-400 sm:block">{r[1]}</span>
                  <span className="tnum font-mono text-[11px] font-medium text-slate-900">{r[2]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function LandingPage() {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yMock = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBg = useTransform(scrollYProgress, [0, 1], [0, -70]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const scrollTo = (hash) => {
    setMenuOpen(false);
    if (lenisRef.current) lenisRef.current.scrollTo(hash, { offset: -72, duration: 1.4 });
  };

  const links = [
    { label: t.nav.features, hash: "#ozellikler" },
    { label: t.nav.modules, hash: "#moduller" },
    { label: t.nav.product, hash: "#urun" },
  ];

  const bentoVisuals = [
    <div key="a" className="mt-6 rounded-xl border border-slate-100 bg-slate-50/50 p-4">
      <MockChart h={130} />
      <div className="mt-2 flex justify-between font-mono text-[9px] text-slate-400">
        {MOCK.map((d) => <span key={d.m}>{t.hero.months[d.m]}</span>)}
      </div>
    </div>,
    <div key="b" className="mt-6 space-y-2">
      {[["Ana İşletme Hesabı", "₺184.250"], ["Vadeli Mevduat", "₺520.000"], ["Kur Hesabı", "$6.400"]].map((r) => (
        <div key={r[0]} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5">
          <span className="text-xs text-slate-600">{r[0]}</span>
          <span className="tnum font-mono text-xs font-medium text-slate-900">{r[1]}</span>
        </div>
      ))}
    </div>,
    <div key="c" className="mt-6 space-y-2">
      {[["Mega İnşaat A.Ş.", true], ["Demir Lojistik Ltd.", true], ["Anadolu Elektrik", false]].map((r) => (
        <div key={r[0]} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5">
          <span className="text-xs text-slate-600">{r[0]}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${r[1] ? "bg-brand-soft text-brand" : "bg-amber-50 text-amber-600"}`}>
            {r[1] ? t.app.customer : t.app.supplier}
          </span>
        </div>
      ))}
    </div>,
    <div key="d" className="mt-6 space-y-2">
      {[["KDV Ödemesi", "12 Haz"], ["Mega İnşaat #1041", "6 Haz"]].map((r) => (
        <div key={r[0]} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5">
          <span className="truncate text-xs text-slate-600">{r[0]}</span>
          <span className="ml-3 shrink-0 rounded-full bg-red-50 px-2 py-0.5 font-mono text-[10px] text-red-600">{r[1]}</span>
        </div>
      ))}
      <div className="flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2.5">
        <span className="text-xs text-emerald-700">Tahsil edildi +₺50.000</span>
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
      </div>
    </div>,
    <div key="e" className="mt-6 flex h-[124px] items-end gap-2 rounded-xl border border-slate-100 bg-slate-50/50 p-4">
      {[[46, 62], [38, 55], [72, 58], [52, 70]].map((b, i) => (
        <div key={i} className="flex flex-1 items-end justify-center gap-1">
          <div className="w-4 rounded-t bg-brand" style={{ height: `${b[0]}%` }} />
          <div className="w-4 rounded-t bg-slate-300" style={{ height: `${b[1]}%` }} />
        </div>
      ))}
    </div>,
  ];

  return (
    <div className="bg-[#FDFDFD] text-slate-900">
      {/* NAV */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/75 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-8 px-5 lg:px-8">
          <Link to="/" data-testid="nav-logo-link" className="shrink-0"><Logo /></Link>
          <nav className="hidden items-center gap-7 md:flex">
            {links.map((l) => (
              <button key={l.hash} onClick={() => scrollTo(l.hash)} data-testid={`nav-link-${l.hash.slice(1)}`}
                className="text-sm text-slate-500 transition-colors hover:text-slate-900">
                {l.label}
              </button>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2.5">
            <div className="hidden items-center rounded-full border border-slate-200 p-0.5 sm:flex" data-testid="lang-toggle">
              {["tr", "en"].map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`lang-toggle-${l}`}
                  className={`rounded-full px-2.5 py-1 font-mono text-[11px] font-medium uppercase transition-colors ${lang === l ? "bg-slate-900 text-white" : "text-slate-400 hover:text-slate-900"}`}>
                  {l}
                </button>
              ))}
            </div>
            {user ? (
              <Link to="/app" data-testid="nav-toapp-link"
                className="hidden rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 sm:block">
                {t.nav.toApp}
              </Link>
            ) : (
              <>
                <Link to="/giris" data-testid="nav-login-link" className="hidden text-sm text-slate-600 transition-colors hover:text-slate-900 sm:block">{t.nav.login}</Link>
                <Link to="/kayit" data-testid="nav-start-cta"
                  className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800">
                  {t.nav.start}
                </Link>
              </>
            )}
            <button className="rounded-md p-2 text-slate-600 hover:bg-slate-100 md:hidden" aria-label="Menü"
              onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-menu-button">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="border-t border-slate-100 bg-white px-5 py-4 md:hidden">
            {links.map((l) => (
              <button key={l.hash} onClick={() => scrollTo(l.hash)} className="block w-full py-2.5 text-left text-sm text-slate-600">
                {l.label}
              </button>
            ))}
            <div className="mt-2 flex items-center gap-3 border-t border-slate-100 pt-3">
              {["tr", "en"].map((l) => (
                <button key={l} onClick={() => setLang(l)} className={`rounded-full px-3 py-1 font-mono text-xs uppercase ${lang === l ? "bg-slate-900 text-white" : "text-slate-400"}`}>{l}</button>
              ))}
              {user ? (
                <Link to="/app" className="ml-auto rounded-md bg-slate-900 px-4 py-2 text-sm text-white">{t.nav.toApp}</Link>
              ) : (
                <Link to="/giris" className="ml-auto text-sm text-slate-600">{t.nav.login}</Link>
              )}
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-32 lg:pt-40">
        <div className="grid-bg pointer-events-none absolute inset-0" />
        <motion.div style={{ y: yBg }} className="pointer-events-none absolute -top-32 right-[-10%] h-[560px] w-[560px] rounded-full bg-gradient-to-b from-brand-soft to-transparent blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t.hero.eyebrow}
              </motion.p>
              <h1 className="mt-6 font-display text-5xl font-medium leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
                <Line delay={0.12}>{t.hero.line1}</Line>
                <Line delay={0.24}>{t.hero.line2}</Line>
                <Line delay={0.36}><span className="text-brand">{t.hero.line3}</span></Line>
              </h1>
              <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.1, ease: EASE, delay: 0.55 }}
                className="mt-8 h-px w-full origin-left bg-slate-200" />
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
                className="mt-8 max-w-md text-base leading-relaxed text-slate-600">
                {t.hero.sub}
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.72 }}
                className="mt-9 flex flex-wrap items-center gap-4">
                <Link to="/kayit" data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 rounded-md bg-slate-900 px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-slate-800">
                  {t.hero.ctaPrimary}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <button onClick={() => scrollTo("#ozellikler")} data-testid="hero-cta-secondary"
                  className="rounded-md border border-slate-200 bg-white px-6 py-3.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50">
                  {t.hero.ctaSecondary}
                </button>
                <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-400">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                  {t.hero.caption}
                </span>
              </motion.div>
            </div>
            <div className="relative lg:col-span-6">
              <motion.div style={{ y: yMock }} initial={{ opacity: 0, y: 48 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.45 }}>
                <MockDashboard />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="mt-20 border-y border-slate-200 bg-white py-5 lg:mt-28">
        <div className="mask-fade-x overflow-hidden">
          <div className="flex w-max animate-marquee">
            {[0, 1].map((half) => (
              <div key={half} aria-hidden={half === 1} className="flex items-center">
                {t.marquee.map((item) => (
                  <span key={`${half}-${item}`} className="flex items-center gap-8 pr-8 font-mono text-xs uppercase tracking-[0.28em] text-slate-400">
                    {item}
                    <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENTO */}
      <section id="ozellikler" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 lg:px-8 lg:py-32">
        <Fade>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">{t.bento.label}</p>
          <div className="mt-4 grid gap-6 lg:grid-cols-12 lg:items-end">
            <h2 className="font-display text-3xl font-medium tracking-tight sm:text-4xl lg:col-span-7 lg:text-5xl">{t.bento.title}</h2>
            <p className="max-w-sm text-sm leading-relaxed text-slate-600 lg:col-span-5">{t.bento.sub}</p>
          </div>
        </Fade>
        <div className="mt-14 grid gap-6 md:grid-cols-6 lg:gap-8">
          {t.bento.items.map((item, i) => (
            <Fade key={item.t} delay={0.05 * i}
              className={i === 0 ? "md:col-span-4" : i === 1 ? "md:col-span-2" : "md:col-span-2"}>
              <div className="group flex h-full flex-col rounded-2xl border border-slate-200/70 bg-white p-7 shadow-sm transition-transform duration-200 hover:-translate-y-0.5 lg:p-8">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] text-slate-400">0{i + 1}</span>
                  <span className="h-1.5 w-1.5 rounded-full bg-brand opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                </div>
                <h3 className="mt-5 font-display text-xl font-medium tracking-tight">{item.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{item.d}</p>
                {bentoVisuals[i]}
              </div>
            </Fade>
          ))}
        </div>
      </section>

      {/* PRODUCT SHOT */}
      <section id="urun" className="scroll-mt-24 border-y border-slate-200 bg-[#F8F9FA] py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Fade className="max-w-2xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">{t.product.label}</p>
            <h2 className="mt-4 font-display text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">{t.product.title}</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600 lg:text-base">{t.product.sub}</p>
          </Fade>
          <Fade delay={0.1} className="relative mt-14">
            <div className="pointer-events-none absolute -inset-x-8 top-10 h-72 rounded-full bg-gradient-to-r from-brand-soft via-white to-brand-soft opacity-80 blur-3xl" />
            <div className="relative rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_40px_120px_-40px_rgba(15,23,42,0.35)]">
              <MockDashboard full />
            </div>
          </Fade>
        </div>
      </section>

      {/* MODULES */}
      <section id="moduller" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 lg:px-8 lg:py-32">
        <Fade>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">{t.modules.label}</p>
          <div className="mt-4 grid gap-6 lg:grid-cols-12 lg:items-end">
            <h2 className="font-display text-3xl font-medium tracking-tight sm:text-4xl lg:col-span-7 lg:text-5xl">{t.modules.title}</h2>
            <p className="max-w-sm text-sm leading-relaxed text-slate-600 lg:col-span-5">{t.modules.sub}</p>
          </div>
        </Fade>
        <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-3">
          {t.modules.items.map((m, i) => (
            <Fade key={m.t} delay={0.05 * i} className="h-full">
              <div className="group flex h-full flex-col bg-white p-8 transition-colors hover:bg-[#F8F9FA]">
                <div className="flex items-center justify-between">
                  {i === 0 && <ArrowLeftRight className="h-5 w-5 text-slate-400 transition-colors group-hover:text-brand" />}
                  {i === 1 && <Landmark className="h-5 w-5 text-slate-400 transition-colors group-hover:text-brand" />}
                  {i === 2 && <Users className="h-5 w-5 text-slate-400 transition-colors group-hover:text-brand" />}
                  {i === 3 && <CalendarClock className="h-5 w-5 text-slate-400 transition-colors group-hover:text-brand" />}
                  {i === 4 && <HandCoins className="h-5 w-5 text-slate-400 transition-colors group-hover:text-brand" />}
                  {i === 5 && <BarChart3 className="h-5 w-5 text-slate-400 transition-colors group-hover:text-brand" />}
                  <span className="font-mono text-[11px] text-slate-300">M{i + 1}</span>
                </div>
                <h3 className="mt-6 font-display text-lg font-medium tracking-tight">{m.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.d}</p>
              </div>
            </Fade>
          ))}
        </div>
      </section>

      {/* ABOUT BAND */}
      <section className="relative overflow-hidden">
        <motion.img
          src="https://images.unsplash.com/photo-1666290439931-42f8f4c275b0?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="İRA Mühendislik"
          className="h-[420px] w-full object-cover lg:h-[520px]"
          initial={{ scale: 1.12 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.6, ease: EASE }}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/70 px-5">
          <Fade className="max-w-3xl text-center">
            <LogoMark className="mx-auto h-12 w-12" />
            <p className="mt-8 font-display text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">{t.about.quote}</p>
            <p className="mt-5 text-sm text-slate-300">{t.about.sub}</p>
          </Fade>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <Fade>
          <div className="rounded-3xl bg-slate-900 px-8 py-16 text-center lg:px-16 lg:py-24">
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
              {t.cta.title}
            </h2>
            <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-slate-400">{t.cta.sub}</p>
            <Link to="/kayit" data-testid="bottom-cta-button"
              className="group mt-10 inline-flex items-center gap-2 rounded-md bg-white px-7 py-3.5 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-100">
              {t.cta.button}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </Fade>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-5 py-14 lg:flex-row lg:items-start lg:justify-between lg:px-8">
          <div>
            <Logo />
            <p className="mt-4 max-w-xs text-xs leading-relaxed text-slate-400">© {new Date().getFullYear()} İRA Mühendislik — {t.footer.rights}</p>
          </div>
          <div className="flex gap-20">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.footer.product}</p>
              {links.map((l) => (
                <button key={l.hash} onClick={() => scrollTo(l.hash)} className="mt-3 block text-sm text-slate-600 hover:text-slate-900">{l.label}</button>
              ))}
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.footer.account}</p>
              <Link to="/giris" className="mt-3 block text-sm text-slate-600 hover:text-slate-900">{t.nav.login}</Link>
              <Link to="/kayit" className="mt-3 block text-sm text-slate-600 hover:text-slate-900">{t.nav.start}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
